import React, { useMemo, useState } from 'react';
import { X, Info, Search, Eye } from 'lucide-react';
import { useCreateRole, useAvailableObjects, type ObjectPermission, type Role, getDefaultSearchPermissions } from '../../../services/queries/roleQueries';
import { useRoles } from '../../../services/queries/roleQueries';
import { useSetupChanges } from '../../../hooks/useSetupChanges';
import { useToast } from '../../../contexts/ToastContext';
import { StandardButton, StandardInput, StandardSelect, StandardTextarea, StandardCheckbox } from '../../standard';
import { RolePermissionPreview } from './RolePermissionPreview';

interface CreateRoleModalProps {
  isOpen: boolean;
  onClose: () => void;
}

export const CreateRoleModal: React.FC<CreateRoleModalProps> = ({ isOpen, onClose }) => {
  const { data: availableObjects } = useAvailableObjects();
  
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    parentRoleId: '',
    permissions: (availableObjects || []).map(obj => ({
      objectId: obj.id,
      objectName: obj.name,
      read: false,
      write: false,
      delete: false
    })) as ObjectPermission[],
    searchPermissions: getDefaultSearchPermissions('Standard User')
  });

  const createRoleMutation = useCreateRole();
  const { data: existingRoles } = useRoles({ includeSystem: true });
  const { logChange } = useSetupChanges();
  const { success, error } = useToast();
  const previewRole = useMemo(() => ({
    id: 'preview-role',
    name: formData.name || 'Preview Role',
    description: formData.description,
    parentRoleId: formData.parentRoleId || undefined,
    isSystem: false,
    permissions: formData.permissions,
    userCount: 0,
    createdDate: new Date().toISOString(),
    createdBy: 'Current User',
    lastModified: new Date().toISOString(),
    searchPermissions: formData.searchPermissions,
  }), [formData]);

  // Update permissions when modal opens and objects are available
  React.useEffect(() => {
    if (isOpen && availableObjects && availableObjects.length > 0 && formData.permissions.length === 0) {
      setFormData(prev => ({
        ...prev,
        permissions: availableObjects.map(obj => ({
          objectId: obj.id,
          objectName: obj.name,
          read: false,
          write: false,
          delete: false
        }))
      }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // Validate role name
    if (!formData.name || formData.name.trim() === '') {
      error('Role name is required', 'Please enter a name for this role');
      return;
    }

    // Validate role name is not just whitespace
    if (formData.name.trim().length === 0) {
      error('Invalid role name', 'Role name cannot be only spaces');
      return;
    }

    // Validate minimum length
    if (formData.name.trim().length < 2) {
      error('Role name too short', 'Role name must be at least 2 characters');
      return;
    }

    try {
      // Find parent role name if selected
      let parentRoleName: string | undefined;
      if (formData.parentRoleId) {
        const parentRole = existingRoles?.find(r => r.id === formData.parentRoleId);
        parentRoleName = parentRole?.name;
      }

      const newRole = await createRoleMutation.mutateAsync({
        name: formData.name.trim(), // Trim whitespace
        description: formData.description,
        parentRoleId: formData.parentRoleId || undefined,
        parentRoleName,
        isSystem: false,
        permissions: formData.permissions,
        searchPermissions: formData.searchPermissions
      });

      // Log the change to setup changes
      logChange({
        action: 'created',
        entityType: 'role',
        entityName: 'Role',
        targetName: newRole.name,
        description: `created role "${newRole.name}"`,
        performedBy: 'Current User'
      });

      success('Role created successfully!');
      onClose();
      resetForm();
    } catch (err: any) {
      error('Failed to create role', err.message || 'Please try again or contact support');
    }
  };

  const resetForm = () => {
    setFormData({
      name: '',
      description: '',
      parentRoleId: '',
      permissions: (availableObjects || []).map(obj => ({
        objectId: obj.id,
        objectName: obj.name,
        read: false,
        write: false,
        delete: false
      })),
      searchPermissions: getDefaultSearchPermissions('Standard User')
    });
  };

  const updatePermission = (objectId: string, field: 'read' | 'write' | 'delete', value: boolean) => {
    setFormData(prev => ({
      ...prev,
      permissions: prev.permissions.map(p => {
        if (p.objectId === objectId) {
          const updated = { ...p, [field]: value };

          // Auto-enable read when write or delete is enabled
          if (field === 'write' && value) {
            updated.read = true;
          }
          if (field === 'delete' && value) {
            updated.read = true;
            updated.write = true;
          }

          // Auto-disable write/delete when read is disabled
          if (field === 'read' && !value) {
            updated.write = false;
            updated.delete = false;
          }

          // Auto-disable delete when write is disabled
          if (field === 'write' && !value) {
            updated.delete = false;
          }

          return updated;
        }
        return p;
      })
    }));
  };

  const setAllPermissions = (field: 'read' | 'write' | 'delete', value: boolean) => {
    setFormData(prev => ({
      ...prev,
      permissions: prev.permissions.map(p => {
        const updated = { ...p, [field]: value };

        // Apply same cascading logic as single permission
        if (field === 'write' && value) {
          updated.read = true;
        }
        if (field === 'delete' && value) {
          updated.read = true;
          updated.write = true;
        }
        if (field === 'read' && !value) {
          updated.write = false;
          updated.delete = false;
        }
        if (field === 'write' && !value) {
          updated.delete = false;
        }

        return updated;
      })
    }));
  };

  // Helper to update search permissions
  const updateSearchPermission = (field: keyof NonNullable<Role['searchPermissions']>, value: boolean) => {
    setFormData(prev => {
      if (!prev.searchPermissions) return prev;

      const updated = { ...prev.searchPermissions, [field]: value };

      // Auto-disable security permissions when system settings is disabled
      if (field === 'canSearchSystemSettings' && !value) {
        updated.canSearchUsers = false;
        updated.canSearchRoles = false;
        updated.canSearchSecurity = false;
      }

      // Update visibleCategories based on enabled permissions
      const newCategories: Array<'objects' | 'workflows' | 'fields' | 'settings'> = [];
      if (updated.canSearchObjects) newCategories.push('objects');
      if (updated.canSearchFields) newCategories.push('fields');
      if (updated.canSearchWorkflows) newCategories.push('workflows');
      if (updated.canSearchSystemSettings) newCategories.push('settings');
      updated.visibleCategories = newCategories;

      return {
        ...prev,
        searchPermissions: updated
      };
    });
  };

  if (!isOpen) return null;

  const allRead = formData.permissions.every(p => p.read);
  const allWrite = formData.permissions.every(p => p.write);
  const allDelete = formData.permissions.every(p => p.delete);

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700 flex items-center justify-between">
          <div>
            <h2 className="text-xl font-bold text-gray-900 dark:text-white">Create New Role</h2>
            <p className="text-sm text-gray-600 dark:text-gray-300 mt-1">
              Define a new role with specific permissions
            </p>
          </div>
          <button
            onClick={() => {
              onClose();
              resetForm();
            }}
            className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="flex-1 overflow-y-auto p-6">
          <div className="space-y-6">
            {/* Basic Information */}
            <div>
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Basic Information</h3>

              <div className="space-y-4">
                <StandardInput
                  label="Role Name"
                  value={formData.name}
                  onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                  placeholder="e.g., Sales Manager"
                  required
                />

                <StandardTextarea
                  label="Description"
                  value={formData.description}
                  onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
                  placeholder="Describe the role's responsibilities..."
                  rows={2}
                />

                <StandardSelect
                  label="Parent Role (Optional)"
                  value={formData.parentRoleId}
                  onChange={(e) => setFormData(prev => ({ ...prev, parentRoleId: e.target.value }))}
                  helpText="Child roles inherit permissions from parent roles"
                  options={[
                    { value: '', label: 'None - This is a top-level role' },
                    ...(existingRoles?.map(role => ({
                      value: role.id,
                      label: role.name
                    })) || [])
                  ]}
                />
              </div>
            </div>

            <RolePermissionPreview roles={[previewRole]} title="Role Preview" />

            {/* Permissions Matrix */}
            <div>
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Object Permissions</h3>

              <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-4 mb-4">
                <div className="flex items-start gap-2">
                  <Info className="w-5 h-5 text-blue-600 dark:text-blue-400 flex-shrink-0 mt-0.5" />
                  <div className="text-sm text-blue-800 dark:text-blue-300">
                    <p className="font-semibold mb-1">Permission Hierarchy:</p>
                    <ul className="list-disc list-inside space-y-1">
                      <li><strong>Read:</strong> View records</li>
                      <li><strong>Write:</strong> Create and edit records (includes Read)</li>
                      <li><strong>Delete:</strong> Delete records (includes Read and Write)</li>
                    </ul>
                  </div>
                </div>
              </div>

              <div className="border border-gray-200 dark:border-gray-700 rounded-lg overflow-hidden">
                <table className="w-full">
                  <thead className="bg-gray-50 dark:bg-gray-900">
                    <tr>
                      <th className="px-4 py-3 text-left text-sm font-medium text-gray-700 dark:text-gray-300">
                        Object
                      </th>
                      <th className="px-4 py-3 text-center text-sm font-medium text-gray-700 dark:text-gray-300">
                        <div className="flex flex-col items-center gap-1">
                          <span>Read</span>
                          <input
                            type="checkbox"
                            checked={allRead}
                            onChange={(e) => setAllPermissions('read', e.target.checked)}
                            className="w-4 h-4 text-purple-600 rounded focus:ring-purple-500"
                            title="Toggle all"
                          />
                        </div>
                      </th>
                      <th className="px-4 py-3 text-center text-sm font-medium text-gray-700 dark:text-gray-300">
                        <div className="flex flex-col items-center gap-1">
                          <span>Write</span>
                          <input
                            type="checkbox"
                            checked={allWrite}
                            onChange={(e) => setAllPermissions('write', e.target.checked)}
                            className="w-4 h-4 text-purple-600 rounded focus:ring-purple-500"
                            title="Toggle all"
                          />
                        </div>
                      </th>
                      <th className="px-4 py-3 text-center text-sm font-medium text-gray-700 dark:text-gray-300">
                        <div className="flex flex-col items-center gap-1">
                          <span>Delete</span>
                          <input
                            type="checkbox"
                            checked={allDelete}
                            onChange={(e) => setAllPermissions('delete', e.target.checked)}
                            className="w-4 h-4 text-purple-600 rounded focus:ring-purple-500"
                            title="Toggle all"
                          />
                        </div>
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                    {formData.permissions.map((permission) => (
                      <tr key={permission.objectId} className="hover:bg-gray-50 dark:hover:bg-gray-700/50">
                        <td className="px-4 py-3 text-sm font-medium text-gray-900 dark:text-white">
                          {permission.objectName}
                        </td>
                        <td className="px-4 py-3 text-center">
                          <input
                            type="checkbox"
                            checked={permission.read}
                            onChange={(e) => updatePermission(permission.objectId, 'read', e.target.checked)}
                            className="w-4 h-4 text-green-600 rounded focus:ring-green-500"
                          />
                        </td>
                        <td className="px-4 py-3 text-center">
                          <input
                            type="checkbox"
                            checked={permission.write}
                            onChange={(e) => updatePermission(permission.objectId, 'write', e.target.checked)}
                            className="w-4 h-4 text-blue-600 rounded focus:ring-blue-500"
                          />
                        </td>
                        <td className="px-4 py-3 text-center">
                          <input
                            type="checkbox"
                            checked={permission.delete}
                            onChange={(e) => updatePermission(permission.objectId, 'delete', e.target.checked)}
                            className="w-4 h-4 text-red-600 rounded focus:ring-red-500"
                          />
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>

            {/* Search Permissions Section */}
            <div>
              <div className="flex items-center gap-2 mb-4">
                <Search className="w-5 h-5 text-purple-600 dark:text-purple-400" />
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Search Permissions</h3>
              </div>

              <div className="bg-purple-50 dark:bg-purple-900/20 border border-purple-200 dark:border-purple-800 rounded-lg p-4 mb-4">
                <div className="flex items-start gap-2">
                  <Info className="w-5 h-5 text-purple-600 dark:text-purple-400 flex-shrink-0 mt-0.5" />
                  <div className="text-sm text-purple-800 dark:text-purple-300">
                    Configure what this role can search in the Global Search (⌘K). Users will only see results for categories they have permission to search.
                  </div>
                </div>
              </div>

              <div className="space-y-4">
                {/* Search Categories */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {/* Objects */}
                  <div className="p-4 border border-gray-200 dark:border-gray-700 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700/30 transition-colors">
                    <StandardCheckbox
                      label="Search Objects"
                      checked={formData.searchPermissions?.canSearchObjects ?? false}
                      onChange={(checked) => updateSearchPermission('canSearchObjects', checked)}
                      helpText="Find custom objects, clients, and records"
                    />
                  </div>

                  {/* Fields */}
                  <div className="p-4 border border-gray-200 dark:border-gray-700 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700/30 transition-colors">
                    <StandardCheckbox
                      label="Search Fields"
                      checked={formData.searchPermissions?.canSearchFields ?? false}
                      onChange={(checked) => updateSearchPermission('canSearchFields', checked)}
                      helpText="Find custom fields across objects"
                    />
                  </div>

                  {/* Workflows */}
                  <div className="p-4 border border-gray-200 dark:border-gray-700 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700/30 transition-colors">
                    <StandardCheckbox
                      label="Search Workflows"
                      checked={formData.searchPermissions?.canSearchWorkflows ?? false}
                      onChange={(checked) => updateSearchPermission('canSearchWorkflows', checked)}
                      helpText="Find workflow rules and automation"
                    />
                  </div>

                  {/* System Settings */}
                  <div className="p-4 border border-gray-200 dark:border-gray-700 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700/30 transition-colors">
                    <StandardCheckbox
                      label="Search System Settings"
                      checked={formData.searchPermissions?.canSearchSystemSettings ?? false}
                      onChange={(checked) => updateSearchPermission('canSearchSystemSettings', checked)}
                      helpText="Find configuration pages and settings"
                    />
                  </div>
                </div>

                {/* Security Settings - Nested under System Settings */}
                {formData.searchPermissions?.canSearchSystemSettings && (
                  <div className="ml-6 pl-4 border-l-2 border-purple-300 dark:border-purple-700">
                    <div className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">
                      Security-Related Settings
                    </div>
                    <div className="p-3 border border-gray-200 dark:border-gray-700 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700/30 transition-colors">
                      <StandardCheckbox
                        label="Search Security Settings"
                        checked={formData.searchPermissions?.canSearchSecurity ?? false}
                        onChange={(checked) => updateSearchPermission('canSearchSecurity', checked)}
                        helpText="Find users, roles, permissions, and security configurations"
                      />
                    </div>
                  </div>
                )}

                {/* Preview Section */}
                <div className="bg-gray-50 dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-lg p-4">
                  <div className="flex items-center gap-2 mb-3">
                    <Eye className="w-4 h-4 text-gray-600 dark:text-gray-400" />
                    <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
                      Search Preview
                    </span>
                  </div>
                  <p className="text-sm text-gray-600 dark:text-gray-400">
                    {formData.searchPermissions && formData.searchPermissions.visibleCategories.length > 0 ? (
                      <>
                        This role can search: <span className="font-medium text-gray-900 dark:text-white">
                          {formData.searchPermissions.visibleCategories.join(', ')}
                        </span>
                      </>
                    ) : (
                      <span className="text-gray-500 dark:text-gray-500">
                        No search access configured
                      </span>
                    )}
                  </p>
                </div>
              </div>
            </div>
          </div>
        </form>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-gray-200 dark:border-gray-700 flex items-center justify-end gap-3 bg-gray-50 dark:bg-gray-900">
          <StandardButton
            type="button"
            variant="ghost"
            onClick={() => {
              onClose();
              resetForm();
            }}
          >
            Cancel
          </StandardButton>
          <StandardButton
            variant="primary"
            onClick={handleSubmit}
            disabled={!formData.name || createRoleMutation.isPending}
            isLoading={createRoleMutation.isPending}
          >
            {createRoleMutation.isPending ? 'Creating...' : 'Create Role'}
          </StandardButton>
        </div>
      </div>
    </div>
  );
};
